import { ref, Ref } from "vue";
import { useCanvaLinkBuilder, CanvaVariant, CanvaCTAIdentifier } from "@/composables/canva_link_builder";
import { hasMagicBrushHdResult, Image, ProcessingState, ResultVariant } from "@/modules/internal_api/image";
import Client from "@/modules/internal_api/client";
import { rbgRedirectToCanvaClickedV101, rbgRedirectToCanvaStartedV101 } from "kaleido-event-tracker";
import { PersistentStore } from "@/stores/persistent_store";
import { Stage } from "konva/lib/Stage";
import { useCheckIOS } from "@/composables/check_if_ios";

export interface CanvaRedirectOptions {
  image: Ref<Image>;
  persistentStore: PersistentStore;
  refreshAllHdLayers: (image: Image) => Promise<void>;
  stage: Ref<any>; // Ref to Stage component with getStage() method
  stageHd: () => Promise<Stage>; // Function that returns Promise to HD stage
  getDataURL: (image: Image, stage: any, element?: any) => string;
  canvaVariant: CanvaVariant;
  canvaCtaIdentifier: CanvaCTAIdentifier;
}

export function useCanvaRedirect(options: CanvaRedirectOptions) {
  const isLoading = ref<boolean>(false);
  const variant = ref<ResultVariant>(ResultVariant.Preview);
  const preview = ref<string>("");

  const flavor = document.querySelector("meta[property='canva-env']")?.getAttribute("content") || "production";
  const { buildUrl } = useCanvaLinkBuilder(flavor);

  const refreshPreview = async (): Promise<void> => {
    const isIOS = useCheckIOS();
    const image = options.image.value;
    const isMagicBrushResult =
      options.persistentStore.previewVariant === ResultVariant.MagicBrushPreview ||
      options.persistentStore.hdVariant === ResultVariant.MagicBrushHd;
    let isHdAvailable = false;

    if (isMagicBrushResult) {
      const iterationId = options.persistentStore.magicBrushCache?.iterationId || image.meta.ai_brush_last_iteration;
      isHdAvailable = hasMagicBrushHdResult(image, iterationId);
      variant.value = isHdAvailable ? ResultVariant.MagicBrushHd : ResultVariant.MagicBrushPreview;
    } else {
      isHdAvailable = image.hdResult?.state === ProcessingState.Finished;
      console.log("image.hdResult?.state", image.hdResult?.state);
      variant.value = isHdAvailable ? ResultVariant.Hd : ResultVariant.Preview;
    }

    if (isHdAvailable && !isIOS) {
      await options.refreshAllHdLayers(image);
      const hdStage = await options.stageHd();
      preview.value = hdStage.toDataURL({
        x: 0,
        y: 0,
        width: image.meta.hdWidth,
        height: image.meta.hdHeight,
      });
    } else {
      // Access the current value of the stage ref
      const stageComponent = options.stage.value;
      if (!stageComponent) {
        throw new Error("Stage is not available yet");
      }
      const konvaStage = stageComponent.getStage();
      preview.value = options.getDataURL(image, konvaStage);
    }
  };

  const redirectToCanva = async () => {
    rbgRedirectToCanvaClickedV101({ image_id: options.image.value.meta.id, source: options.canvaCtaIdentifier });
    isLoading.value = true;

    await refreshPreview();

    const image = options.image.value;
    try {
      const result = await Client.uploadToBucket(image, preview.value, variant.value);

      if (result.success) {
        const url = buildUrl(result.url, options.canvaVariant, options.canvaCtaIdentifier);
        // Safari on iOS is blocking all window.open calls inside async functions
        setTimeout(() => {
          rbgRedirectToCanvaStartedV101({ image_id: options.image.value.meta.id, source: options.canvaCtaIdentifier });
          window.open(url, "_blank");
        });
      } else {
        throw new Error("Upload to bucket failed");
      }
    } catch (error) {
      console.error("Error uploading to bucket", error);
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    redirectToCanva,
  };
}
